import { useEffect, useState } from "react";
import { useAuth } from "../auth/context";
import { useReplicache } from "../replicache/context";

// Spec §8.4 sync indicator.  Combines two signals:
//   - Replicache's own onSync(true/false) — true while a push or pull is in
//     flight to the server.
//   - The local pending-mutation queue — non-empty means we have writes that
//     the server hasn't acknowledged yet (might be debouncing the push, might
//     be offline, might just be slow).
//
// Polling every second for the queue is cheap (the count is read straight
// out of memory) and avoids the complexity of wiring into Replicache's
// internal events for queue mutations.

const POLL_MS = 1_000;

type Tone = "green" | "amber" | "blue" | "slate";

export function SyncBadge() {
  const rep = useReplicache();
  const { state } = useAuth();

  const [syncing, setSyncing] = useState(false);
  const [pending, setPending] = useState(0);

  // Hook into Replicache's onSync slot.  Only one consumer at a time — the
  // app currently has no other subscribers, so we own this slot.  Restore on
  // unmount/instance-change.
  useEffect(() => {
    if (!rep) {
      setSyncing(false);
      setPending(0);
      return;
    }
    const prev = rep.onSync;
    rep.onSync = (s) => setSyncing(s);
    return () => {
      rep.onSync = prev;
    };
  }, [rep]);

  // Poll the queue length.
  useEffect(() => {
    if (!rep) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const list = await rep.experimentalPendingMutations();
        if (!cancelled) setPending(list.length);
      } catch {
        // Replicache closed mid-poll — ignore.
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [rep]);

  const offline =
    state.status === "offline" || state.status === "reauth_required";

  // One transient notice when we *become* offline, so users learn that
  // edits keep working and sync later (the badge alone is easy to miss).
  // ReauthBanner already covers the reauth_required case.
  const [showOfflineNotice, setShowOfflineNotice] = useState(false);
  const isPlainOffline = state.status === "offline";
  useEffect(() => {
    if (!isPlainOffline) {
      setShowOfflineNotice(false);
      return;
    }
    setShowOfflineNotice(true);
    const t = setTimeout(() => setShowOfflineNotice(false), 7000);
    return () => clearTimeout(t);
  }, [isPlainOffline]);

  let tone: Tone;
  let label: string;
  let pulse = false;
  if (offline) {
    tone = "slate";
    label =
      state.status === "reauth_required"
        ? "Sign-in expired"
        : pending > 0
          ? `Offline · ${pending} queued`
          : "Offline";
  } else if (syncing) {
    tone = "blue";
    label = "Syncing…";
    pulse = true;
  } else if (pending > 0) {
    tone = "amber";
    label = `${pending} pending`;
  } else {
    tone = "green";
    label = "Synced";
  }

  // Tailwind needs the literal class names to make it into the build, so
  // resolve from a static map rather than templating colour strings.
  const dotColour: Record<Tone, string> = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    blue: "bg-sky-500",
    slate: "bg-slate-400",
  };
  const textColour: Record<Tone, string> = {
    green: "text-emerald-700 dark:text-emerald-300",
    amber: "text-amber-800 dark:text-amber-300",
    blue: "text-sky-700 dark:text-sky-300",
    slate: "text-slate-600 dark:text-slate-300",
  };

  const titleParts = [label];
  if (rep) titleParts.push(`Pending mutations: ${pending}`);
  if (syncing) titleParts.push("A push or pull is in flight.");
  if (offline) titleParts.push("Local writes will sync on reconnect.");

  const onSyncNow = () => {
    if (!rep) return;
    // Fire-and-forget; the onSync slot will flip the badge while it runs.
    // push first so any local mutations make it up before we pull state down.
    void rep.push();
    void rep.pull();
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs ${textColour[tone]}`}
      title={titleParts.join("\n")}
      aria-live="polite"
    >
      <span
        className={`inline-block h-2 w-2 rounded-full ${dotColour[tone]} ${
          pulse ? "animate-pulse" : ""
        }`}
        aria-hidden
      />
      {label}
      {rep && (
        <button
          type="button"
          onClick={onSyncNow}
          disabled={syncing}
          className="ml-1 rounded border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
          title="Force a push + pull right now"
        >
          Sync now
        </button>
      )}
      {showOfflineNotice && (
        <span
          role="status"
          className="safe-inset fixed bottom-4 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-3 py-2 text-sm font-normal text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
        >
          Working offline — your changes are saved here and will sync when
          you're back.
        </span>
      )}
    </span>
  );
}
